import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

const fadeUp = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

export default function PaymentResultShell({
    testId,
    loading,
    loadingText,
    tone = "neutral",
    icon,
    badge,
    title,
    description,
    children,
    actions,
}) {
    const ring =
        tone === "success"
            ? "border-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.15)]"
            : tone === "error"
              ? "border-rose-400/30 shadow-[0_0_40px_rgba(251,113,133,0.12)]"
              : "border-white/10";

    return (
        <div
            className="relative min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-24 lg:py-28"
            data-testid={testId}
        >
            <div className="absolute inset-0 grid-bg opacity-[0.18] pointer-events-none" />
            <div className="absolute inset-0 radial-glow pointer-events-none opacity-60" />
            <div
                className={`absolute top-1/4 left-1/2 -translate-x-1/2 w-[28rem] h-48 rounded-full blur-3xl pointer-events-none ${
                    tone === "success"
                        ? "bg-emerald-500/15"
                        : tone === "error"
                          ? "bg-rose-500/12"
                          : "bg-cyan-500/8"
                }`}
            />

            <motion.div
                className="relative w-full max-w-lg"
                {...fadeUp}
            >
                <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/40">
                    {loading ? (
                        <div className="py-10 flex flex-col items-center gap-5 text-center">
                            <div
                                className={`w-16 h-16 rounded-2xl border ${ring} flex items-center justify-center bg-black/40`}
                            >
                                <Loader2 className="w-7 h-7 animate-spin text-cyan-300" />
                            </div>
                            <p className="text-zinc-400 font-light">
                                {loadingText}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="text-center">
                                {badge && (
                                    <p className="font-code text-[11px] uppercase tracking-[0.28em] text-cyan-300/80 mb-5">
                                        {badge}
                                    </p>
                                )}
                                <div
                                    className={`mx-auto mb-6 w-16 h-16 rounded-2xl border ${ring} flex items-center justify-center bg-black/50`}
                                >
                                    {icon}
                                </div>
                                <h1 className="font-display text-3xl sm:text-[2rem] font-semibold text-white tracking-tight">
                                    {title}
                                </h1>
                                {description && (
                                    <p className="mt-3 text-zinc-400 font-light leading-relaxed max-w-md mx-auto">
                                        {description}
                                    </p>
                                )}
                            </div>

                            {children}

                            {actions && (
                                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                                    {actions}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
